import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {DatePickerInput as PeacockDatePickerInput} from '@pepsico-ds/ui';
import {useDatePickerInput} from '@/components/atoms/DatePickerInput/datePickerInput-hook';
import {DATE_VALIDATION_MESSAGE} from '@/utils/constants/commonConstants';
import PropTypes from 'prop-types';

const DatePickerInput = ({value, updateValue, uiSchema, id = ''}) => {
  const {
    label,
    placeholder = 'mm/dd/yyyy',
    required = true,
    className,
    validationRules = [],
    disabled = false,
    disabledHelperText = DATE_VALIDATION_MESSAGE.END_DATE_HELPER_TEXT,
    dependentDate,
    minDate: customMinDate,
    maxDate: customMaxDate,
    menuPosition = 'bottom-right'
  } = uiSchema;

  const {validateDates} = useDatePickerInput(label, validationRules);
  const [isError, setIsError] = useState(false);
  const [helperText, setHelperText] = useState('');

  const startYear = useMemo(() => {
    if (customMinDate) return customMinDate.getFullYear();
    return new Date().getFullYear() - 30;
  }, [customMinDate]);

  const endYear = useMemo(() => {
    if (customMaxDate) return customMaxDate.getFullYear();
    return new Date().getFullYear() + 200;
  }, [customMaxDate]);

  const datePickerMenuConfig = useMemo(() => {
    const config = {};
    if (customMinDate || customMaxDate) {
      config.disabled = {};
      if (customMinDate) config.disabled.beforeDate = customMinDate;
      if (customMaxDate) config.disabled.afterDate = customMaxDate;
    }
    return config;
  }, [customMinDate, customMaxDate]);

  const dependentDateKey = Array.isArray(dependentDate)
    ? dependentDate.join('|')
    : dependentDate;

  useEffect(() => {
    if ((dependentDate || value) && !disabled) {
      handleStartDateEndDateValidation(value);
    } else if (!value) {
      clearValidationError(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentDateKey, value]);

  const handleValidationError = useCallback(
    (errorMsg, val) => {
      setHelperText(errorMsg);
      updateValue(val, true);
      setIsError(true);
    },
    [updateValue]
  );

  const clearValidationError = useCallback(
    (val) => {
      updateValue(val, false);
      setIsError(false);
      setHelperText('');
    },
    [updateValue]
  );

  const handleStartDateEndDateValidation = useCallback(
    (val) => {
      const errorMsg = validateDates(val);
      if (errorMsg) {
        handleValidationError(errorMsg, val);
      } else {
        clearValidationError(val);
      }
    },
    [validateDates, handleValidationError, clearValidationError]
  );

  const onDateSelect = useCallback(
    (formattedDate) => {
      const val = formattedDate || '';
      if (required && !val) {
        setIsError(true);
        setHelperText('Date is required');
        updateValue('', true);
        return;
      }
      handleStartDateEndDateValidation(val);
    },
    [required, handleStartDateEndDateValidation, updateValue]
  );

  const displayHelperText = useMemo(() => {
    if (isError && helperText?.length > 0) return helperText;
    if (disabled && disabledHelperText) return disabledHelperText;
    return '';
  }, [isError, helperText, disabled, disabledHelperText]);

  const feedbackType = useMemo(() => {
    if (isError && helperText?.length > 0) return 'error';
    if (disabled && disabledHelperText) return 'info';
    return 'info';
  }, [isError, helperText, disabled, disabledHelperText]);

  return (
    <div
      className={className}
      data-testid={id ? 'date_picker_input_' + id : 'date_picker_input'}>
      <PeacockDatePickerInput
        label={label}
        placeholder={placeholder}
        selected={value || undefined}
        setSelected={(formattedDate) => onDateSelect(formattedDate)}
        format='mm/dd/yyyy'
        disabled={disabled}
        error={isError}
        helperText={displayHelperText}
        feedbackType={feedbackType}
        startYear={startYear}
        endYear={endYear}
        datePickerMenu={datePickerMenuConfig}
        menuPosition={menuPosition}
        data-cy={id ? 'date_picker_input_' + id : 'date_picker_input'}
      />
    </div>
  );
};

DatePickerInput.propTypes = {
  value: PropTypes.string,
  updateValue: PropTypes.func.isRequired,
  uiSchema: PropTypes.shape({
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    className: PropTypes.string,
    validationRules: PropTypes.array,
    disabled: PropTypes.bool,
    disabledHelperText: PropTypes.string,
    dependentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    hideFeedbackIcon: PropTypes.bool,
    menuPosition: PropTypes.oneOf([
      'top-left',
      'top-right',
      'bottom-left',
      'bottom-right'
    ])
  }).isRequired,
  id: PropTypes.string
};

export default React.memo(DatePickerInput);
